import { useLocale } from "../lib/i18n";

export function HowToPlay() {
  const { t, isRTL } = useLocale();

  return (
    <div
      dir={isRTL ? "rtl" : "ltr"}
      className="px-1 pt-5 pb-8 flex flex-col gap-6"
      style={{ color: "var(--parchment-soft)" }}
    >
      <div className="text-center">
        <p
          className="mb-1"
          style={{
            fontFamily: "'Heebo', sans-serif",
            fontSize: 11,
            fontWeight: 600,
            color: "var(--gold-400)",
            letterSpacing: "0.15em",
            textTransform: "uppercase",
          }}
        >
          {t("howto.rules")}
        </p>
        <h1
          className="font-serif"
          style={{
            fontSize: 28,
            fontWeight: 700,
            color: "var(--gold-300)",
            lineHeight: 1.15,
          }}
        >
          {t("howto.title")}
        </h1>
        <p
          className="mt-2 leading-relaxed whitespace-pre-line"
          style={{
            fontFamily: "'Heebo', sans-serif",
            fontSize: 14,
            color: "var(--text-secondary)",
          }}
        >
          {t("howto.subtitle")}
        </p>
      </div>

      <div className="flex flex-col gap-3">
        <Step number={1} title={t("howto.step1Title")} description={t("howto.step1Desc")} />
        <Step number={2} title={t("howto.step2Title")} description={t("howto.step2Desc")} />
        <Step number={3} title={t("howto.step3Title")} description={t("howto.step3Desc")} />
        <Step number={4} title={t("howto.step4Title")} description={t("howto.step4Desc")} />
      </div>

      <div
        className="rounded-2xl p-4"
        style={{
          background: "rgba(250, 245, 232, 0.06)",
          border: "1px solid var(--border)",
        }}
      >
        <p
          className="mb-3"
          style={{
            fontFamily: "'Heebo', sans-serif",
            fontSize: 11,
            fontWeight: 600,
            color: "var(--gold-400)",
            letterSpacing: "0.15em",
            textTransform: "uppercase",
          }}
        >
          {t("howto.scoring")}
        </p>
        <div className="grid grid-cols-7 gap-1 text-center">
          {["♪1", "♪2", "♪3", "♪4", "♪5", "♪6", t("howto.miss")].map((label) => (
            <div
              key={label}
              style={{
                fontFamily: "'Heebo', sans-serif",
                fontSize: 11,
                fontWeight: 700,
                color: "var(--text-muted)",
              }}
            >
              {label}
            </div>
          ))}
          {[6, 5, 4, 3, 2, 1, 0].map((pts) => (
            <div
              key={pts}
              className="font-serif"
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: pts === 6 ? "var(--gold-300)" : pts === 0 ? "#c16a6a" : "var(--parchment-soft)",
              }}
            >
              {pts}
            </div>
          ))}
        </div>
      </div>

      <div>
        <p
          className="mb-3 px-1"
          style={{
            fontFamily: "'Heebo', sans-serif",
            fontSize: 11,
            fontWeight: 600,
            color: "var(--gold-400)",
            letterSpacing: "0.15em",
            textTransform: "uppercase",
          }}
        >
          {t("howto.gameModes")}
        </p>
        <div className="flex flex-col gap-2">
          <ModeRow title={t("howto.dailyTitle")} description={t("howto.dailyDesc")} />
          <ModeRow title={t("howto.endlessTitle")} description={t("howto.endlessDesc")} />
          <ModeRow title={t("howto.playlistTitle")} description={t("howto.playlistDesc")} />
          <ModeRow title={t("howto.weeklyTitle")} description={t("howto.weeklyDesc")} />
        </div>
      </div>
    </div>
  );
}

function Step({
  number,
  title,
  description,
}: {
  number: number;
  title: string;
  description: string;
}) {
  return (
    <div
      className="rounded-2xl p-4 flex gap-3 items-start"
      style={{
        background: "rgba(250, 245, 232, 0.06)",
        border: "1px solid var(--border)",
      }}
    >
      <div
        className="flex-shrink-0 font-serif"
        style={{
          minWidth: 28,
          fontSize: 22,
          fontWeight: 700,
          color: "var(--gold-300)",
          lineHeight: 1,
          paddingTop: 2,
          textAlign: "center",
        }}
      >
        {number}.
      </div>
      <div className="flex-1">
        <p
          className="font-serif"
          style={{ fontSize: 15, fontWeight: 700, color: "var(--parchment-soft)" }}
        >
          {title}
        </p>
        <p
          dir="auto"
          className="mt-1 leading-relaxed whitespace-pre-line"
          style={{
            fontFamily: "'Heebo', sans-serif",
            fontSize: 12,
            color: "var(--text-secondary)",
          }}
        >
          {description}
        </p>
      </div>
    </div>
  );
}

function ModeRow({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div
      className="rounded-xl p-3.5"
      style={{
        background: "rgba(250, 245, 232, 0.04)",
        border: "1px solid var(--border)",
      }}
    >
      <p
        className="font-serif"
        style={{ fontSize: 15, fontWeight: 700, color: "var(--gold-300)" }}
      >
        {title}
      </p>
      <p
        dir="auto"
        className="mt-1 leading-relaxed whitespace-pre-line"
        style={{
          fontFamily: "'Heebo', sans-serif",
          fontSize: 12,
          color: "var(--text-secondary)",
        }}
      >
        {description}
      </p>
    </div>
  );
}
